import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, TemplateRef, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { upperFirst } from 'lodash';
import { plural } from 'pluralize';
import { TemplatingService } from '../../services/templating/templating.service';
import { formatDateForDisplay } from '../../utils/date-format/date-format.utils';
import {
  HeadingComponentConfig, BreadcrumbOption, ButtonComponentConfig,
  DropdownMenuComponentConfig, SwitchComponentConfig, CrudTableViewConfig,
  CrudFormConfig, EntitySelectionConfig,
  SelectableOptionWithTooltip,
  Action,
  ActionBehavior,
  HeadingType,
  RedirectUrlMode,
  InputGroupComponentConfig,
  IconType,
  IconComponentConfig,
  CrudViewColumn,
  CrudTableViewColumn,
  ButtonType,
  HorizontalPosition,
  TextComponentConfig,
  CrudViewColumnType
} from '../../utils';
import { DEFAULT_CREATE_BUTTON_ICON, DEFAULT_CREATE_BUTTON_TITLE_VERB } from './config/constants';
import {
  CARD_LIST_DELETE_ITEM_DEFAULT_ACTIONS,
  CARD_LIST_EDIT_ITEM_DEFAULT_ACTIONS,
  CARD_LIST_EDIT_ITEM_DEFAULT_ACTIONS_EMIT,
  LIST_GRID_ITEM_DEFAULT_ACTIONS,
  LIST_GRID_ITEM_DEFAULT_ACTIONS_EMIT
} from './config/list-grid-config';
import { CardListConfig } from './model/card-data-map-config';
import { ColumnNumber, ColumnOrder, ItemActions, ListMode, ViewMode } from './model/enums';
import { EntityConfigInterface } from './model/entity';
import { GridBuilderComponent } from '../grid-builder/grid-builder/grid-builder.component';
import { FormBuilderComponent } from '../edit-form/components/form-builder/form-builder.component';
import { GridBuilderButtonConfig } from './utils/grid-builder-button-config.interface';
import { CardBuilderComponent } from '../card-builder/card-builder/card-builder.component';
import { DataDetailViewBuilderComponent } from '../data-detail-view-builder/data-detail-view-builder.component';

import * as lodash from 'lodash';

@Component({
  selector: 'rappider-crud-view',
  templateUrl: './crud-view.component.html',
  styleUrls: ['./crud-view.component.scss']
})
export class RappiderCrudViewComponent implements OnInit, OnChanges {

  @Input() activeEntityConfig: EntityConfigInterface;
  @Input() entityConfigs: EntityConfigInterface;
  @Input() entityData: any | any[];

  // #region rappider-title-toolbar Inputs

  @Input() modelNameSingular: string;
  @Input() mainTitleConfig: HeadingComponentConfig;
  @Input() mainTitleButtonConfig: ButtonComponentConfig;
  @Input() mainTitleConfigs?: Record<string, HeadingComponentConfig> = {};
  /* flag to display or hide the toolbar */
  @Input() displayToolbar = false;
  /* explicit option to pass to the toolbar in order to set the visibility of back button */
  @Input() displayToolbarBackButton = false;
  /* buttons to be displayed in the toolbar */
  @Input() toolbarButtons?: ButtonComponentConfig[] = [];
  // Display grid/card list buttons
  @Input() displayListModeRadioButtons? = true;
  /* flag to display breadcrumb under title */
  @Input() displayBreadCrumb = true;
  @Input() displayGripButton = false;
  @Input() breadCrumbOptions: BreadcrumbOption[] | string[] | string;
  @Input() titleBarActionButtons?: ButtonComponentConfig[] = [];
  @Input() isTitleBarBehaviorRoute?: boolean;
  @Input() showCreateButton?: boolean = true;
  @Input() showCreateButtonEmit?: boolean;
  @Input() showEditButtonEmit?: boolean;
  @Input() createButtonTitle?: string;
  @Input() titleBarActionMenu?: DropdownMenuComponentConfig;
  @Input() titleBarSwitchSettings: SwitchComponentConfig;
  /* custom template */
  @Input() customTemplate!: TemplateRef<any>;
  /* detail view title bar action buttons */
  @Input() detailViewTitleBarActionButtons?: ButtonComponentConfig[] = [];
  /* list view title bar action buttons */
  @Input() listViewTitleBarActionButtons?: ButtonComponentConfig[] = [];

  @Output() titleBarActionButtonClicked = new EventEmitter<ButtonComponentConfig>();
  @Output() titleBarActionButtonConfirm = new EventEmitter<ButtonComponentConfig>();
  @Output() toolbarActionButtonClicked = new EventEmitter<ButtonComponentConfig | any>();
  @Output() toolbarBackButtonClicked = new EventEmitter<ButtonComponentConfig | any>();
  @Output() gripButtonClicked = new EventEmitter<string>();
  @Output() doneClick = new EventEmitter();


  // #endregion

  // #region rappider-list-grid Inputs

  @Input() listGridConfig: CrudTableViewConfig;
  @Input() listGridData: any[];
  @Input() isListGridActionButtonsEmit: boolean;
  /* if true, add edit and delete button for list grid item */
  @Input() addListGridItemDefaultActions = true;
  @Input() isListGridDataLoading: boolean;
  /* col span border */
  @Input() isListGridBorderless: boolean;
  /* redirection url data to be replaced in specified url template */
  @Input() redirectUrlData: Record<string, any>;
  /* Displays or hides the filter row */
  @Input() showFiltersRow?: boolean = false;
  @Input() joyride?: { joyrideStep: string; title: string; text: string };
  @Input() gridBuilderButton?: boolean | GridBuilderButtonConfig = false;
  @Input() cardBuilderButton?: boolean | ButtonComponentConfig = false;
  @Input() itemDataBuilderButton?: boolean | ButtonComponentConfig = false;
  @Input() showFormBuilderButton?: boolean = false;
  @Output() itemDeleted = new EventEmitter<{ action: Action; data: any }>();
  @Output() listActionClick = new EventEmitter<any>();
  @Output() gridBuilderConfigSaved = new EventEmitter<CrudTableViewConfig>();
  @Output() cardBuilderConfigSaved = new EventEmitter<CardListConfig>();
  @Output() itemDataBuilderConfigSaved = new EventEmitter<CrudFormConfig>();
  @Output() listGridVisibilityColumnsChanged = new EventEmitter<string[]>();
  @Output() formBuilderConfigSaved = new EventEmitter<{ formType: 'create' | 'update'; config: CrudFormConfig }>();

  // #endregion

  // #region rappider-card-list Inputs

  @Input() cardListConfig: CardListConfig;
  /* if true, add edit and delete button for the card item */
  @Input() addCardItemDefaultActions = true;

  @Output() gridListItemSelected = new EventEmitter<any>();
  @Output() gridListItemActionClicked = new EventEmitter<any>();
  @Output() cardListItemSelected = new EventEmitter<any>();
  @Output() cardListItemEditing = new EventEmitter<any>();
  @Output() cardListItemActionClicked = new EventEmitter<any>();
  @Output() cardCcsRenderCompleted = new EventEmitter<boolean>();

  // #endregion

  // #region rappider-edit-form Inputs

  /* configuration of the create form */
  @Input() createFormConfig: CrudFormConfig;
  /* data for default input values */
  @Input() createFormDefaultData: any;
  /* auto submit&dirty value for form */
  @Input() createFormSubmitted: boolean;
  /* loading status for submit button */
  @Input() createFormSubmitButtonLoading: boolean;
  /* dynamic data for select box */
  @Input() createFormDynamicDataForSelectBox: EntitySelectionConfig[];

  @Output() createFormSubmit = new EventEmitter<any>();

  // #endregion

  // #region rappider-edit-form Inputs

  /* configuration of the edit/update form */
  @Input() updateFormConfig: CrudFormConfig;
  /* data for default input values */
  @Input() updateFormData: any;
  /* auto submit&dirty value for form */
  @Input() updateFormSubmitted: boolean;
  /* loading status for submit button */
  @Input() updateFormSubmitButtonLoading: boolean;
  /* dynamic data for selectbox */
  @Input() updateFormDynamicDataForSelectBox: EntitySelectionConfig[];

  @Output() updateFormSubmit = new EventEmitter<any>();

  // #endregion

  /* #region configuration of the detail view */
  @Input() detailViewConfig: CrudFormConfig;
  // #endregion

  // #region internal Inputs

  /* view mode for the crud pages */
  @Input() viewMode?: ViewMode = ViewMode.List;
  /* list mode for the view page: options are grid or card view */
  @Input() listMode?: ListMode = ListMode.Card;

  @Input() loadingMessage? = 'Fetching data, please wait....';

  /* active item and id are used in detail view */
  @Input() activeItem: any;
  @Input() activeItemId?: string;
  @Output() viewModeChanged = new EventEmitter<ViewMode>();

  // #endregion

  ViewMode = ViewMode;
  ListMode = ListMode;
  IconType = IconType;
  ButtonType = ButtonType;

  /* ViewChild for grid builder component */
  @ViewChild(GridBuilderComponent) gridBuilderComponent: GridBuilderComponent;

  /* ViewChild for card builder component */
  @ViewChild(CardBuilderComponent) cardBuilderComponent: CardBuilderComponent;

  /* ViewChild for item data builder component */
  @ViewChild(DataDetailViewBuilderComponent) itemDataBuilderComponent: DataDetailViewBuilderComponent;

  /* ViewChild for form builder components */
  @ViewChild('createFormBuilder') createFormBuilderComponent: FormBuilderComponent;
  @ViewChild('updateFormBuilder') updateFormBuilderComponent: FormBuilderComponent;

  /* flag to render the page when all config is ready */
  loading = true;

  /* Grid builder related properties */
  gridBuilderConfig: any = {};

  /* Form builder related properties */
  formBuilderConfig: { create?: CrudFormConfig; update?: CrudFormConfig } = {};

  /* Card builder related properties */
  cardBuilderConfig: any = {};

  /* Item data builder related properties */
  itemDataBuilderConfig: CrudFormConfig = { items: [] };

  /* radio button settings for grid and card views */
  titleBarRadioButtonSettings: SelectableOptionWithTooltip[] = [];

  cardListSearchText: string;
  /* card data is filtered listGridData according to the search text (and possible filters) */
  cardSearchInputConfig: InputGroupComponentConfig = {
    textbox: {
      placeholder: 'Type to search'
    },
    suffixIcon: {
      name: 'fas fa-search',
      type: IconType.FontAwesome
    }
  };

  cardSubtitles: { [key: string]: HeadingComponentConfig[] } = {};
  filteredData: any[] = [];

  detailViewToolbarButtons: ButtonComponentConfig[] = [
    {
      key: ColumnOrder.OneColumn,
      behavior: ActionBehavior.Emit,
      icon: {
        name: 'fa fa-regular fa-list',
        type: IconType.FontAwesome
      },
      tooltipText: 'One Column'
    },
    {
      key: ColumnOrder.TwoColumn,
      behavior: ActionBehavior.Emit,
      icon: {
        name: 'fa fa-regular fa-table-cells-large',
        type: IconType.FontAwesome
      },
      tooltipText: 'Two Columns'
    },
    {
      key: ColumnOrder.ThreeColumn,
      behavior: ActionBehavior.Emit,
      icon: {
        name: 'fa fa-regular fa-table-cells',
        type: IconType.FontAwesome
      },
      tooltipText: 'Three Columns'
    }
  ];
  crudFieldsDisplayColumnNumber = 2; // Default to 2 columns, can be adjusted based on requirements

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private templatingService: TemplatingService
  ) { }

  ngOnInit(): void {
    this.initComponent();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes?.listGridData && changes?.listGridData?.currentValue) ||
      (changes?.entityData && changes?.entityData?.currentValue)
    ) {
      if (this.listMode === ListMode.Card) {
        this.filteredData = this.getFilteredCardData();
        // if (this.filteredData?.length) {
        //   this.setSubTitles();
        // }
      }
    }

    if (changes?.viewMode && changes?.viewMode?.currentValue) {
      this.setViewMode(changes.viewMode.currentValue);
    }

    if (changes?.activeEntityConfig && changes?.activeEntityConfig?.currentValue && changes.activeEntityConfig.currentValue !== changes.activeEntityConfig.previousValue) {
      this.activeEntityConfig = changes.activeEntityConfig.currentValue;
      this.initComponent();
    }

    if (changes?.activeItem && changes?.activeItem?.currentValue) {
      if (this.viewMode === ViewMode.Detail) {
        this.initComponent();
      }
    }

    if (changes?.cardListConfig) {
      if (this.cardListConfig) {
        this.cardListConfig = this.restoreFunctions(this.cardListConfig);
      }
      this.convertIconConfigToIconFn();
      if (changes.cardListConfig.currentValue !== changes.cardListConfig.previousValue) {
        this.setListGridItemDefaultActionButtons();
      }
    }

    if (changes?.detailViewConfig && changes?.detailViewConfig?.currentValue) {
      // Update column number from detailViewConfig sections
      this.updateColumnNumberFromDetailViewConfig(changes.detailViewConfig.currentValue);
    }
  }

  /**
   * Update column number from detailViewConfig sections
   * @param detailViewConfig The detail view configuration
   */
  private updateColumnNumberFromDetailViewConfig(detailViewConfig: CrudFormConfig): void {
    if (detailViewConfig?.sections && detailViewConfig.sections.length > 0) {
      const firstSection = detailViewConfig.sections[0];
      if (firstSection?.columns) {
        if (firstSection.columns === 1) {
          this.crudFieldsDisplayColumnNumber = ColumnNumber.OneColumn;
        } else if (firstSection.columns === 2) {
          this.crudFieldsDisplayColumnNumber = ColumnNumber.TwoColumn;
        } else if (firstSection.columns === 3) {
          this.crudFieldsDisplayColumnNumber = ColumnNumber.ThreeColumn;
        }
      }
    }
  }


  initComponent() {
    this.setTitleBarCreateActionButton();
    this.setListModeRadioButtons();
    this.setListGridItemDefaultActionButtons();
    this.setFilteredCardData();
    // this.setBreadCrumbOptions();
    this.setViewMode(this.viewMode);
    // this.setSubTitles();
    this.convertIconConfigToIconFn();

    if (this.detailViewConfig) {
      this.updateColumnNumberFromDetailViewConfig(this.detailViewConfig);
    }

    this.loading = false;
  }

  /**
   * Convert iconConfig to iconFn at runtime (for backward compatibility)
   * iconConfig is serializable, iconFn is not
   * Always use iconConfig if available, even if iconFn exists
   */
  private convertIconConfigToIconFn(): void {
    if (!this.cardListConfig?.iconConfig) {
      return;
    }
    // iconConfig is now used directly, iconFn kept for backward compatibility
    // No conversion needed as iconConfig is used directly in template
  }

  /**
   * Get icon config for card item
   */
  getCardIconConfig(item: any): IconComponentConfig | null {
    if (this.cardListConfig?.iconConfig) {
      return this.cardListConfig.iconConfig;
    }
    // Fallback to iconFn for backward compatibility
    if (this.cardListConfig?.iconFn) {
      const iconName = this.cardListConfig.iconFn(item);
      return {
        name: iconName,
        type: IconType.FontAwesome
      };
    }
    return null;
  }

  /* filter listGridData according to the cardListSearchValue */
  getFilteredCardData() {
    if (this.cardListSearchText?.trim() &&
      this.listGridData?.length &&
      this.listGridConfig
    ) {
      let filteredData;
      if (this.listGridConfig.multipleSearchFields && this.listGridConfig.multipleSearchFields.length > 0) {
        filteredData = this.listGridData.filter(item =>
          this.listGridConfig.multipleSearchFields.some(field => item[field]?.toLowerCase().includes(this.cardListSearchText.trim().toLowerCase()))
        );
      } else if (this.listGridConfig.defaultSearchField) {
        filteredData = this.listGridData.filter(item =>
          item[this.listGridConfig.defaultSearchField]?.toLowerCase().includes(this.cardListSearchText.trim().toLowerCase())
        );
      } else {
        // there is no default search field, and data is set to single search field
        // we will check if the data has name or title fields
        if (this.listGridData[0] && (this.listGridData[0].name || this.listGridData[0].title)) {
          filteredData = this.listGridData.filter(item =>
            item.name?.toLowerCase().includes(this.cardListSearchText.trim().toLowerCase()) ||
            item.title?.toLowerCase().includes(this.cardListSearchText.trim().toLowerCase())
          );
        } else {
          const firstField = Object.keys(this.listGridData[0])[0];
          let searchField = firstField;
          if (firstField === 'id') {
            // if the first field is id, we will check the second field
            const secondField = Object.keys(this.listGridData[0])[1];
            searchField = secondField;
          }
          if (searchField) {
            // we will filter using the first field which is not the id
            filteredData = this.listGridData.filter(item =>
              item[Object.keys(item)[0]]?.toLowerCase().includes(this.cardListSearchText.trim().toLowerCase())
            );
          }
        }
      }
      return filteredData || [];
    }
    return this.listGridData || [];
  }

  setFilteredCardData() {
    this.filteredData = this.getFilteredCardData();
  }

  // setBreadCrumbOptions() {
  //   if (!this.breadCrumbOptions?.length && this.displayBreadCrumb) {
  //     this.breadCrumbOptions = [
  //       { label: this.modelNameSingular, emitEvent: true, data: { viewMode: ViewMode.List } },
  //     ];
  //   }
  // }

  setViewMode(viewMode: ViewMode) {
    this.setTitleBarCreateActionButton();
    this.viewMode = viewMode;
    const createTitle = this.activeEntityConfig?.config?.createItemTitle || 'Add ' + this.modelNameSingular;
    const updateTitle = this.activeEntityConfig?.config?.updateItemTitle || 'Edit ' + this.modelNameSingular;
    const listTitle = this.activeEntityConfig?.config?.listItemsTitle || plural(this.modelNameSingular);
    const detailTitle = this.activeItem ? this.activeItem[this.activeEntityConfig?.config?.titleFieldName || 'title'] ||
      this.activeItem?.name || this.activeItem?.key || this.activeItem?.id || this.activeItem?._id : this.modelNameSingular;

    switch (viewMode) {
      case ViewMode.Create:
        this.mainTitleConfig = <HeadingComponentConfig>{ content: createTitle, type: HeadingType.H3 };
        this.displayBreadCrumb = true;
        this.breadCrumbOptions = [
          { label: plural(this.modelNameSingular), emitEvent: true, data: { viewMode: ViewMode.List } },
          { label: 'Add ' + this.modelNameSingular }
        ];
        break;
      case ViewMode.Edit:
        this.mainTitleConfig = <HeadingComponentConfig>{ content: updateTitle, type: HeadingType.H3 };
        this.displayBreadCrumb = true;
        this.breadCrumbOptions = [
          { label: plural(this.modelNameSingular), emitEvent: true, data: { viewMode: ViewMode.List } },
          { label: 'Edit ' + this.modelNameSingular }
        ];
        break;
      case ViewMode.List:
        this.mainTitleConfig = <HeadingComponentConfig>{ content: listTitle, type: HeadingType.H3 };
        this.displayBreadCrumb = false;
        this.breadCrumbOptions = [];
        break;
      case ViewMode.Detail:
        this.mainTitleConfig = <HeadingComponentConfig>{ content: detailTitle, type: HeadingType.H3 };
        this.displayBreadCrumb = true;
        this.breadCrumbOptions = [
          { label: plural(this.modelNameSingular), emitEvent: true, data: { viewMode: ViewMode.List } },
          { label: detailTitle }
        ];
        break;
      default:
        this.mainTitleConfig = <HeadingComponentConfig>{ content: listTitle, type: HeadingType.H3 };
        this.displayBreadCrumb = false;
        this.breadCrumbOptions = [];
        break;
    }
    this.viewModeChanged.emit(viewMode);
    this.setListModeRadioButtons();
  }

  setTitleBarCreateActionButton() {
    // if there is an existing create button, we will use it
    let existingCreateButtonConfig: Action | undefined;
    if (this.activeEntityConfig?.config?.listGridConfig?.listActions?.length) {
      existingCreateButtonConfig = this.activeEntityConfig?.config?.listGridConfig?.listActions?.find(i => i.id === ItemActions.Create);
    }

    // if there is an existing create button in the card list config, we will use it
    if (existingCreateButtonConfig) {
      // if the create button is already in the title bar action buttons, we will not add it again
      const existingCreateButton = this.titleBarActionButtons?.find(i => i.key === existingCreateButtonConfig.id);
      if (!existingCreateButton) {
        this.titleBarActionButtons.push(<ButtonComponentConfig>{
          key: ItemActions.Create,
          type: existingCreateButtonConfig.buttonType || ButtonType.Primary,
          text: existingCreateButtonConfig.text || this.createButtonTitle || `${DEFAULT_CREATE_BUTTON_TITLE_VERB} ${upperFirst(this.modelNameSingular?.toLowerCase() || '')}`,
          icon: existingCreateButtonConfig.icon || { name: DEFAULT_CREATE_BUTTON_ICON, color: 'white' },
          behavior: existingCreateButtonConfig.behavior || ActionBehavior.Emit,
          redirectUrl: existingCreateButtonConfig.redirectUrl,
          joyride: existingCreateButtonConfig.joyride
        });
      }

    } else { // if there is no existing create button, we will create a new one

      if (this.showCreateButton && !this.isTitleBarBehaviorRoute) {
        const existingCreateButton = this.titleBarActionButtons?.find(i => i.key === 'create-item');
        if (existingCreateButton) {
          this.titleBarActionButtons = this.titleBarActionButtons.filter(i => i.key !== 'create-item');
        }
        this.titleBarActionButtons.push(<ButtonComponentConfig>{
          key: 'create-item',
          text: this.createButtonTitle || `${DEFAULT_CREATE_BUTTON_TITLE_VERB}`,
          icon: { name: DEFAULT_CREATE_BUTTON_ICON },
          behavior: ActionBehavior.Emit,
        });
      } else if (this.showCreateButton && this.isTitleBarBehaviorRoute) {
        const existingCreateButton = this.titleBarActionButtons?.find(i => i.key === 'create-item');
        if (existingCreateButton) {
          this.titleBarActionButtons = this.titleBarActionButtons.filter(i => i.key !== 'create-item');
        }
        this.titleBarActionButtons.push(<ButtonComponentConfig>{
          key: 'create-item',
          text: this.createButtonTitle || `${DEFAULT_CREATE_BUTTON_TITLE_VERB} ${upperFirst(this.modelNameSingular?.toLowerCase() || '')}`,
          icon: { name: DEFAULT_CREATE_BUTTON_ICON },
          behavior: ActionBehavior.Route,
          redirectUrl: '/create'
        });
      }
    }

    // Add form builder button if enabled
    if (this.showFormBuilderButton) {
      const existingFormBuilderButton = this.titleBarActionButtons?.find(i => i.key === 'form-builder');
      if (!existingFormBuilderButton) {
        this.titleBarActionButtons.push(<ButtonComponentConfig>{
          key: 'form-builder',
          text: '',
          icon: { name: 'fa fa-gear', type: IconType.FontAwesome },
          behavior: ActionBehavior.Emit,
          tooltipText: 'Form Builder Settings',
          horizontalPosition: HorizontalPosition.Right
        });
      }
    }
  }

  setListModeRadioButtons() {
    if (this.viewMode === ViewMode.List && this.displayListModeRadioButtons !== false) {
      this.titleBarRadioButtonSettings = [
        {
          key: '<i class="fa-light fa-table-list"></i>',
          value: 'grid-view',
          tooltipText: 'Grid View'
        },
        {
          key: '<i class="fa-light fa-grid-2"></i>',
          value: 'card-view',
          tooltipText: 'Card View'
        }
      ];
    } else {
      this.titleBarRadioButtonSettings = [];
    }
  }

  /* Add edit and delete action buttons for list grid item */
  setListGridItemDefaultActionButtons(): void {
    if (this.addListGridItemDefaultActions && this.listGridConfig) {
      const updatedConfig = { ...this.listGridConfig };
      if (this.listGridConfig.itemActions?.length) {
        updatedConfig.itemActions = [
          ...this.listGridConfig.itemActions.filter(i => i.name !== ItemActions.Edit && i.name !== ItemActions.Delete),
          ...LIST_GRID_ITEM_DEFAULT_ACTIONS
        ];
      } else if (this.isListGridActionButtonsEmit) {
        updatedConfig.itemActions = LIST_GRID_ITEM_DEFAULT_ACTIONS_EMIT;
      } else {
        updatedConfig.itemActions = LIST_GRID_ITEM_DEFAULT_ACTIONS;
      }

      // Add gear button for table settings if grid builder is enabled
      if (this.gridBuilderButton) {
        const isConfig = typeof this.gridBuilderButton === 'object';
        const buttonConfig: GridBuilderButtonConfig = isConfig ? this.gridBuilderButton as GridBuilderButtonConfig : {};

        // Set icon color to white if button type is Primary and color not specified
        const iconConfig = buttonConfig.icon ? { ...buttonConfig.icon } : {
          name: 'fa fa-gear',
          type: IconType.FontAwesome
        };

        if (buttonConfig.type === ButtonType.Primary && !iconConfig.color) {
          iconConfig.color = 'white';
        }

        const gearButton: Action = {
          name: 'table-settings',
          icon: iconConfig,
          text: buttonConfig.text,
          buttonType: buttonConfig.type,
          behavior: ActionBehavior.Emit,
          tooltipText: buttonConfig.tooltipText || 'Table Settings',
          horizontalPosition: HorizontalPosition.Right
        };

        const listActions = updatedConfig.listActions ? [...updatedConfig.listActions] : [];
        const existingGearButton = listActions.find(action => action.name === 'table-settings');
        if (!existingGearButton) {
          listActions.push(gearButton);
        }
        updatedConfig.listActions = listActions;
      }
      this.listGridConfig = updatedConfig;
    }
    if (this.addCardItemDefaultActions && this.cardListConfig) {
      const updatedCardConfig = { ...this.cardListConfig };
      if (!('deleteItemActionButton' in updatedCardConfig) || updatedCardConfig.deleteItemActionButton === undefined) {
        updatedCardConfig.deleteItemActionButton = CARD_LIST_DELETE_ITEM_DEFAULT_ACTIONS;
      }
      if (!('editItemActionButton' in updatedCardConfig) || updatedCardConfig.editItemActionButton === undefined) {
        updatedCardConfig.editItemActionButton = this.showEditButtonEmit
          ? CARD_LIST_EDIT_ITEM_DEFAULT_ACTIONS_EMIT
          : CARD_LIST_EDIT_ITEM_DEFAULT_ACTIONS;
      }
      this.cardListConfig = updatedCardConfig;
    }
  }


  onListGridListActionDropdownItemClick(item: any): void {
  }

  onListGridColumnActionClick(actionData: { action: Action; data: any }): void {
    if (actionData.action?.name === ItemActions.Edit) {
      this.gridListItemActionClicked.emit(actionData);
      if (actionData?.action.behavior === ActionBehavior.CrudEditLink) {
        this.setViewMode(ViewMode.Edit);
        this.updateFormData = { ...this.activeItem, ...actionData.data };
        this.cardListItemEditing.emit(actionData.data);
      }
    } else if (actionData.action?.name === ItemActions.Delete) {
      this.itemDeleted.emit(actionData);
    }
  }

  onListActionClick(actionData: any): void {
    if (actionData?.action?.name === 'table-settings') {
      this.openGridBuilder();
    } else {
      this.listActionClick.emit(actionData);
    }
  }

  onUpdateFormValueChange(value: any): void {
  }

  onUpdateFormSubmit(value: any): void {
    this.updateFormSubmit.emit(value);
  }

  onCreateFormValueChange(value: any): void {
  }

  onRelatedItemDeleted(value: any): void {
    console.log(value);
  }

  onCreateFormSubmit(value: any): void {
    this.createFormSubmit.emit(value);
  }

  onRelatedItemTitleBarActionButtonClicked(action: any): void {
    console.log(action);
  }

  onTitleBarActionButtonClick(action: ButtonComponentConfig | any) {
    if (action?.key === ItemActions.Create) {
      if (action?.behavior === ActionBehavior.Route) {
        if (action?.redirectUrl) {
          if (action?.redirectUrlMode === RedirectUrlMode.NavigateToAbsolutePath) {
            this.router.navigate([action?.redirectUrl]);
          } else {
            this.router.navigate([action?.redirectUrl], { relativeTo: this.route });
          }
        } else {
          this.router.navigate(['create'], { relativeTo: this.route });
        }
        this.titleBarActionButtonClicked.emit(action);
      } else if (action.behavior === ActionBehavior.Emit) {
        // emit the action
        this.titleBarActionButtonClicked.emit(action);
        // change the view mode to create
        this.setViewMode(ViewMode.Create);
      }
    } else if (action?.key === 'form-builder') {
      if (this.viewMode === ViewMode.Create) {
        this.openCreateFormBuilder();
      } else if (this.viewMode === ViewMode.Edit || this.viewMode === ViewMode.Update) {
        this.openUpdateFormBuilder();
      } else {
        this.openCreateFormBuilder();
      }
    } else {
      if (action?.behavior === ActionBehavior.Route) {
        if (action?.redirectUrl) {
          if (action?.redirectUrlMode === RedirectUrlMode.NavigateToAbsolutePath) {
            this.router.navigate([action?.redirectUrl]);
          } else {
            this.router.navigate([action?.redirectUrl], { relativeTo: this.route });
          }
        }
      } else if (action?.behavior === ActionBehavior.Emit) {
        this.titleBarActionButtonClicked.emit(action);
      }
    }
  }

  onTitleBarActionButtonConfirm(action: any): void {
    this.titleBarActionButtonConfirm.emit(action);
  }

  onBreadCrumbOptionClicked(option: BreadcrumbOption) {
    if (option) {
      if (option.data?.viewMode) {
        switch (option.data.viewMode) {
          case ViewMode.Create:
            this.viewMode = ViewMode.Create;
            this.mainTitleConfig = this.mainTitleConfigs?.create || <HeadingComponentConfig>{ content: 'Add', type: HeadingType.H3 };
            break;
          case ViewMode.Edit:
            this.viewMode = ViewMode.Edit;
            this.mainTitleConfig = this.mainTitleConfigs?.edit || <HeadingComponentConfig>{ content: 'Edit', type: HeadingType.H3 };
            break;
          default:
            this.setViewMode(ViewMode.List);
            this.mainTitleConfig = this.mainTitleConfigs?.list || <HeadingComponentConfig>{ content: 'List', type: HeadingType.H3 };
            break;
        }
      }
    }
  }

  onTitleBarRadioButtonClick(selectedValue: string) {
    if (selectedValue === 'card-view') {
      this.listMode = ListMode.Card;
      this.filteredData = this.getFilteredCardData();
      // if (this.filteredData?.length && !this.isSubTitlesCalculated()) {
      //   this.setSubTitles();
      // }
    } else {
      this.listMode = ListMode.Grid;
    }
  }

  /* returns subtitles array for card view */
  getCardSubtitles(cardData: any): HeadingComponentConfig[] {
    // If getSubTitlesFunction is provided, use it to generate subtitles
    if (this.cardListConfig?.getSubTitlesFunction) {
      return this.cardListConfig.getSubTitlesFunction(cardData);
    }
    // Otherwise, use subtitleFieldNames
    if (this.cardListConfig?.subtitleFieldNames?.length) {
      const result = this.cardListConfig.subtitleFieldNames.map(subtitleFieldName => {
        // Support dot notation for nested fields with lodash
        let value = lodash.get(cardData, subtitleFieldName) || '';

        // Format date fields if they are date/datetime type
        if (value && this.activeEntityConfig?.config?.listGridConfig?.columns) {
          const column = this.activeEntityConfig.config.listGridConfig.columns.find(
            col => col.fieldName === subtitleFieldName
          );
          if (column && (column.type === CrudViewColumnType.Date || column.type === CrudViewColumnType.DateTime)) {
            value = formatDateForDisplay(
              value,
              column.type as CrudViewColumnType.Date | CrudViewColumnType.DateTime
            );
          }
        }

        return <HeadingComponentConfig>{
          content: Array.isArray(value) ? value.join(', ') : String(value),
          type: HeadingType.H6
        };
      });
      return result;
    }
    return [];
  }

  /**
   * Get card action button with disabled state based on item's permission flag
   */
  private getCardActionButton(item: any, buttonType: 'edit' | 'delete'): Action | null {
    const button = buttonType === 'edit'
      ? this.cardListConfig?.editItemActionButton
      : this.cardListConfig?.deleteItemActionButton;
    if (!button) {
      return null;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const visible = (button as any)?.visible;
    if (visible === false) {
      return null;
    }

    const flag = buttonType === 'edit' ? 'isUpdatable' : 'isDeletable';
    return item?.[flag] === false ? { ...button, disabled: true } : button;
  }

  /**
   * Get edit button for card item with disabled state based on item's isUpdatable
   */
  getCardEditButton(item: any): Action | null {
    return this.getCardActionButton(item, 'edit');
  }

  /**
   * Get delete button for card item with disabled state based on item's isDeletable
   */
  getCardDeleteButton(item: any): Action | null {
    return this.getCardActionButton(item, 'delete');
  }

  /**
   * Get description text config for card view
   */
  getCardDescription(cardData: any): TextComponentConfig | null {
    if (this.cardListConfig?.getDescriptionFunction) {
      const result = this.cardListConfig.getDescriptionFunction(cardData);
      if (result === null || result === undefined) {
        return null;
      }
      if (Array.isArray(result)) {
        if (result.length === 0) {
          return null;
        }
        const firstItem = result[0];
        if (firstItem && typeof firstItem === 'object' && firstItem.content !== undefined) {
          return {
            content: firstItem.content,
            textMode: firstItem.textMode
          };
        }
        return {
          content: result as any // eslint-disable-line @typescript-eslint/no-explicit-any
        };
      }
      if (typeof result === 'object' && result.content !== undefined) {
        return result;
      }
      if (typeof result === 'string') {
        const text = result.trim();
        return text ? { content: text, text } : null;
      }
      if (typeof result === 'object' && result !== null) {
        return {
          content: result as any // eslint-disable-line @typescript-eslint/no-explicit-any
        };
      }
      return null;
    }

    // Otherwise, use descriptionFieldName or default description field
    let value = this.cardListConfig?.descriptionFieldName
      ? lodash.get(cardData, this.cardListConfig.descriptionFieldName)
      : cardData?.description;
    if (!value || value === '') {
      return null;
    }

    // Format date fields if they are date/datetime type
    if (value && this.activeEntityConfig?.config?.listGridConfig?.columns) {
      const fieldName = this.cardListConfig?.descriptionFieldName || 'description';
      const column = this.activeEntityConfig.config.listGridConfig.columns.find(
        col => col.fieldName === fieldName
      );
      if (column && (column.type === CrudViewColumnType.Date || column.type === CrudViewColumnType.DateTime)) {
        value = formatDateForDisplay(
          value,
          column.type as CrudViewColumnType.Date | CrudViewColumnType.DateTime
        );
      }
    }

    if (typeof value === 'object') {
      return { content: value as any }; // eslint-disable-line @typescript-eslint/no-explicit-any
    }
    const text = String(value).trim();
    return text ? { content: text, text } : null;
  }

  /**
   * Restore functions from backend
   * Converts function strings back to executable functions
   */
  private restoreFunctions(config: CardListConfig): CardListConfig {
    if (!config) {
      return config;
    }

    const restored = { ...config };

    // Convert function strings back to functions
    if (restored.descriptionFunctionString && !restored.getDescriptionFunction) {
        // eslint-disable-next-line no-eval
        restored.getDescriptionFunction = eval(`(${restored.descriptionFunctionString})`);
    }

    if (restored.subtitleFunctionString && !restored.getSubTitlesFunction) {
      // eslint-disable-next-line no-eval
      restored.getSubTitlesFunction = eval(`(${restored.subtitleFunctionString})`);
    }

    return restored;
  }

  /* returns alert config for card view */
  getCardAlertConfig(cardData: any) {
    if (this.cardListConfig?.alertConfig) {
      return this.cardListConfig?.alertConfig(cardData);
    } else {
      return null;
    }
  }

  // isSubTitlesCalculated(): boolean {
  //   if (this.filteredData?.length && !(this.cardSubtitles[this.filteredData[0]?.id] || this.cardSubtitles[this.filteredData[0]?._id])) {
  //     return false;
  //   }
  //   return true;
  // }

  // setSubTitles() {
  //   if (this.listGridData?.length) {
  //     const idField = this.getIdField(this.listGridData[0]);
  //     if(idField) {
  //     this.listGridData?.forEach((item) => {
  //       if (item && item[idField]) {
  //         this.cardSubtitles[item[idField]] = this.getCardSubtitles(item);
  //       }
  //     });
  //     }
  //   }
  // }

  getIdField(item: any): string | null {
    if (!item) {
      return null;
    }
    // Return the id field based on the active entity config or default to 'id' or '_id'
    if (this.activeEntityConfig?.config?.idFieldName && item[this.activeEntityConfig?.config?.idFieldName]) {
      return this.activeEntityConfig?.config?.idFieldName;
    } else if (item.id) {
      return 'id';
    } else if (item._id) {
      return '_id';
    } else {
      // If no id field is found, return null
      return null;
    }
  }

  onSearchInputChange() {
    this.setFilteredCardData();
  }

  onCardViewActionClick(action: any, cardData: any) {
    const buttonAction = action?.button || action;
    if (buttonAction?.behavior === ActionBehavior.CrudEditLink) {
      // let routingViewMode = ViewMode.Edit;
      this.setViewMode(ViewMode.Edit);
      this.updateFormData = { ...this.activeItem, ...cardData };
      this.cardListItemEditing.emit(cardData);
      // const queryParams = {
      //   viewMode: routingViewMode,
      //   [this.activeEntityConfig?.config?.idFieldName || 'id']: cardData?.id,
      //   entityName: this.activeEntityConfig?.name
      // };
      // this.router.navigate([buttonAction?.routerLink], { relativeTo: this.route, queryParams });
    }

    if (buttonAction?.behavior === ActionBehavior.Route) {
      this.redirectToUrlByAction(buttonAction, cardData);
    } else if (buttonAction?.behavior === ActionBehavior.Emit) {
      if (buttonAction?.name === ItemActions.Delete) {
        this.itemDeleted.emit({ action: buttonAction, data: cardData });
      } else {
        this.cardListItemActionClicked.emit({ action: buttonAction, data: cardData });
      }
    }
  }

  onCardViewItemSelected(item: any) {
    if (this.cardListConfig?.cardItemClickBehavior === ActionBehavior.Route) {
      this.redirectToUrlByAction(
        {
          behavior: ActionBehavior.Route, redirectUrlMode: RedirectUrlMode.Navigate,
          redirectUrl: this.cardListConfig?.cardItemRouterLink || ''
        },
        item
      );
    } else {
      this.cardListItemSelected.emit(item);
    }
  }

  onSelectionChange(item) {
    this.gridListItemSelected.emit(item);
  }

  redirectToUrlByAction(action: Action, data: any) {
    /* navigate */
    let url = action.redirectUrl;
    /* set id if specified */
    if (action.redirectUrl && data?.id) {
      url = this.templatingService.execTemplate(action.redirectUrl, data);
    }
    if (action?.redirectUrlMode === 'navigate') {
      this.router.navigate([url], { relativeTo: this.route });
    } else {
      this.router.navigateByUrl(url);
    }
  }

  onBackButtonClick(timestamp: string) {
    this.setViewMode(ViewMode.List);
    this.toolbarBackButtonClicked.emit(timestamp);
  }

  onToolbarActionButtonClick(button: ButtonComponentConfig) {
    if (button) {
      if (button.behavior === ActionBehavior.Route) {
        this.router.navigate([button.redirectUrl], { relativeTo: this.route });
      } else {
        this.toolbarActionButtonClicked.emit(button);
      }
      if (button.key === ColumnOrder.OneColumn) {
        this.crudFieldsDisplayColumnNumber = ColumnNumber.OneColumn;
      } else if (button.key === ColumnOrder.TwoColumn) {
        this.crudFieldsDisplayColumnNumber = ColumnNumber.TwoColumn;
      } else if (button.key === ColumnOrder.ThreeColumn) {
        this.crudFieldsDisplayColumnNumber = ColumnNumber.ThreeColumn;
      }
    }
  }

  onLinkCellClicked(event: { column: CrudTableViewColumn; data: any }) {
    if (event?.column?.linkBehavior === ActionBehavior.CrudDetailLink) {
      this.setViewMode(ViewMode.Detail);
    }
  }

  onGripButtonClicked(event: any) {
    this.gripButtonClicked.emit(event);
  }

  onCardCcsRenderCompleted(renderCompleted) {
    this.cardCcsRenderCompleted.emit(renderCompleted);
  }

  /**
   * Handle grid builder configuration save
   */
  onGridBuilderConfigSave(updatedConfig: any) {
    this.gridBuilderConfig = updatedConfig;

    const finalConfig = { ...updatedConfig };
    if (!finalConfig.listActions) {
      finalConfig.listActions = [];
    }

    // Restore all original list actions from the input config
    if (this.listGridConfig && this.listGridConfig.listActions) {
      finalConfig.listActions = [...this.listGridConfig.listActions];
    }

    const existingGearButton = finalConfig.listActions.find(action => action.name === 'table-settings');
    if (!existingGearButton && this.gridBuilderButton) {
      const isConfig = typeof this.gridBuilderButton === 'object';
      const buttonConfig: GridBuilderButtonConfig = isConfig ? this.gridBuilderButton as GridBuilderButtonConfig : {};

      // Set icon color to white if button type is Primary and color not specified
      const iconConfig = buttonConfig.icon ? { ...buttonConfig.icon } : {
        name: 'fa fa-gear',
        type: IconType.FontAwesome
      };

      if (buttonConfig.type === ButtonType.Primary && !iconConfig.color) {
        iconConfig.color = 'white';
      }

      const gearButton: Action = {
        name: 'table-settings',
        icon: iconConfig,
        text: buttonConfig.text,
        buttonType: buttonConfig.type,
        behavior: ActionBehavior.Emit,
        tooltipText: buttonConfig.tooltipText || 'Table Settings',
        horizontalPosition: HorizontalPosition.Right
      };
      finalConfig.listActions.push(gearButton);
    }

    if (this.activeEntityConfig && this.activeEntityConfig.config) {
      this.activeEntityConfig.config.listGridConfig = finalConfig;
    }
    this.gridBuilderConfigSaved.emit(finalConfig);
  }

  /**
   * Open grid builder for table settings
   */
  openGridBuilder() {
    if (this.gridBuilderButton && this.gridBuilderComponent) {
      const configForBuilder: CrudTableViewConfig = this.listGridConfig ?
        { ...this.listGridConfig } :
        { columns: [], title: {}, listActions: [], itemActions: [] };

      // Remove all list actions from configForBuilder so they don't appear in the grid builder
      // This includes the gear button and any other list actions from the input config
      if (configForBuilder.listActions) {
        configForBuilder.listActions = [];
      }
      if (this.listGridConfig?.visibilityColumns) {
        configForBuilder.visibilityColumns = [...this.listGridConfig.visibilityColumns];
      }

      this.gridBuilderConfig = configForBuilder;
      this.gridBuilderComponent.openEditModal();
    }
  }

  onJoyrideDoneButtonClick(){
    this.doneClick.emit();
  }

  onListGridVisibilityColumnsChanged(visibilityColumns: string[]) {
    if (this.listGridConfig) {
      this.listGridConfig = {
        ...this.listGridConfig,
        visibilityColumns: [...visibilityColumns]
      };
    }
    this.listGridVisibilityColumnsChanged.emit(visibilityColumns);
  }

  /**
   * Get default form configuration
   * @param formType The form type (create or update)
   * @returns Default form configuration
   */
  private getDefaultFormConfig(formType: 'create' | 'update'): CrudFormConfig {
    return {
      items: [],
      formLayout: 'vertical' as any,
      submitButton: {
        visible: true,
        text: formType === 'create' ? 'Create' : 'Update',
        type: 'primary' as any,
        size: 'default' as any,
        block: false
      }
    };
  }

  /**
   * Open form builder for create form
   */
  openCreateFormBuilder() {
    if (this.showFormBuilderButton && this.createFormBuilderComponent) {
      const configForBuilder: CrudFormConfig = this.createFormConfig
        ? { ...this.createFormConfig }
        : this.getDefaultFormConfig('create');

      this.formBuilderConfig.create = configForBuilder;
      this.createFormBuilderComponent.openEditModal();
    }
  }

  /**
   * Open form builder for update form
   */
  openUpdateFormBuilder() {
    if (this.showFormBuilderButton && this.updateFormBuilderComponent) {
      const configForBuilder: CrudFormConfig = this.updateFormConfig
        ? { ...this.updateFormConfig }
        : this.getDefaultFormConfig('update');

      this.formBuilderConfig.update = configForBuilder;
      this.updateFormBuilderComponent.openEditModal();
    }
  }

  /**
   * Handle form builder configuration save
   */
  onFormBuilderConfigSave(formType: 'create' | 'update', config: CrudFormConfig) {
    const finalConfig = { ...config };

    // Update local form configs
    if (formType === 'create') {
      this.createFormConfig = finalConfig;
      this.formBuilderConfig.create = finalConfig;
    } else if (formType === 'update') {
      this.updateFormConfig = finalConfig;
      this.formBuilderConfig.update = finalConfig;
    }
    this.updateActiveEntityConfig(formType, finalConfig);
    this.formBuilderConfigSaved.emit({ formType, config: finalConfig });
  }

  /**
   * Update active entity config with form configuration
   * @param formType The form type (create or update)
   * @param config The form configuration
   */
  private updateActiveEntityConfig(formType: 'create' | 'update', config: CrudFormConfig) {
    if (!this.activeEntityConfig?.config) {
      return;
    }

    if (formType === 'create') {
      this.activeEntityConfig.config.createItemFormConfig = config;
    } else if (formType === 'update') {
      this.activeEntityConfig.config.updateItemFormConfig = config;
    }
  }

  /**
   * Handle card builder configuration save
   */
  onCardBuilderConfigSave(updatedConfig: CardListConfig): void {
    this.cardBuilderConfig = updatedConfig;
    if (this.activeEntityConfig?.config) {
      this.activeEntityConfig.config.cardListConfig = updatedConfig;
    }
    this.cardBuilderConfigSaved.emit(updatedConfig);
  }

  /**
   * Get card builder button config if available
   */
  get cardBuilderButtonConfig(): ButtonComponentConfig | null {
    return (this.cardBuilderButton && typeof this.cardBuilderButton === 'object')
      ? this.cardBuilderButton as ButtonComponentConfig
      : null;
  }

  /**
   * Get entity fields for card builder from various config sources
   */
  private getEntityFieldsForCardBuilder(): SelectableOptionWithTooltip[] {
    const fields: SelectableOptionWithTooltip[] = [];
    const seen = new Set<string>();

    const addFields = (items: Array<{ fieldName?: string; title?: string }> = []) => {
      items.filter(i => i.fieldName && !seen.has(i.fieldName)).forEach(item => {
        seen.add(item.fieldName);
        fields.push({
          key: item.fieldName,
          value: item.fieldName,
          tooltipText: item.title || item.fieldName
        });
      });
    };

    addFields((this.listGridConfig || this.activeEntityConfig?.config?.listGridConfig)?.columns);
    addFields((this.createFormConfig || this.activeEntityConfig?.config?.createItemFormConfig)?.items);
    addFields((this.updateFormConfig || this.activeEntityConfig?.config?.updateItemFormConfig)?.items);

    return fields.sort((a, b) =>
      (a.tooltipText || a.key || a.value || '').localeCompare(b.tooltipText || b.key || b.value || '')
    );
  }

  /**
   * Open card builder for card settings
   */
  openCardBuilder(): void {
    if (!this.cardBuilderButton || !this.cardBuilderComponent) {
      return;
    }

    const config: CardListConfig = this.cardListConfig || {
      titleFieldName: '',
      descriptionFieldName: '',
      subtitleFieldNames: [],
      cardItemClickBehavior: ActionBehavior.Emit,
      showSearchWithAiButton: false
    };

    this.cardBuilderConfig = { ...config };
    this.cardBuilderComponent.entityFields = this.getEntityFieldsForCardBuilder();
    this.cardBuilderComponent.apiPath =
      this.activeEntityConfig?.apiConfig?.getAll?.url ||
      this.activeEntityConfig?.apiConfig?.baseUrl?.split('/').pop();
    this.cardBuilderComponent.writeValue(config);
    this.cardBuilderComponent.openEditModal();
  }

  /**
   * Handle item data builder configuration save
   */
  onItemDataBuilderConfigSave(updatedConfig: CrudFormConfig): void {
    this.itemDataBuilderConfig = updatedConfig;
    if (this.activeEntityConfig?.config) {
      this.activeEntityConfig.config.detailViewConfig = updatedConfig;
    }
    this.itemDataBuilderConfigSaved.emit(updatedConfig);
  }

  /**
   * Get item data builder button config if available
   */
  get itemDataBuilderButtonConfig(): ButtonComponentConfig | null {
    return (this.itemDataBuilderButton && typeof this.itemDataBuilderButton === 'object')
      ? this.itemDataBuilderButton as ButtonComponentConfig
      : null;
  }

  /**
   * Get entity fields for item data builder from various config sources
   */
  private getEntityFieldsForItemDataBuilder(): SelectableOptionWithTooltip[] {
    const fields: SelectableOptionWithTooltip[] = [];
    const seen = new Set<string>();

    const addFields = (items: Array<{ fieldName?: string; title?: string }> = []) => {
      items.filter(i => i.fieldName && !seen.has(i.fieldName)).forEach(item => {
        seen.add(item.fieldName);
        fields.push({
          key: item.fieldName,
          value: item.fieldName,
          tooltipText: item.title || item.fieldName
        });
      });
    };

    addFields((this.listGridConfig || this.activeEntityConfig?.config?.listGridConfig)?.columns);
    addFields((this.createFormConfig || this.activeEntityConfig?.config?.createItemFormConfig)?.items);
    addFields((this.updateFormConfig || this.activeEntityConfig?.config?.updateItemFormConfig)?.items);
    addFields((this.detailViewConfig || this.activeEntityConfig?.config?.detailViewConfig)?.items);

    return fields.sort((a, b) =>
      (a.tooltipText || a.key || a.value || '').localeCompare(b.tooltipText || b.key || b.value || '')
    );
  }

  /**
   * Open item data builder for detail view settings
   */
  openItemDataBuilder(): void {
    if (!this.itemDataBuilderButton || !this.itemDataBuilderComponent) {
      return;
    }

    const config: CrudFormConfig = this.detailViewConfig || {
      items: [],
      formLayout: 'vertical' as any
    };

    this.itemDataBuilderConfig = { ...config };
    this.itemDataBuilderComponent.writeValue(config);
    if (this.activeItem) {
      this.itemDataBuilderComponent.previewData = this.activeItem;
    } else if (this.listGridData && this.listGridData.length > 0) {
      this.itemDataBuilderComponent.previewData = this.listGridData[0];
    }
    this.itemDataBuilderComponent.openEditModal();
  }

}
